import { useCallback, useImperativeHandle, useState } from 'react'
import {
  checkInputErrorDni,
  checkInputErrorMaxLength,
  checkInputErrorRequired,
  formatDNI,
  useDatePicker,
  useInput,
  useRadio,
  useSelect,
  useTextarea
} from '@chilecompra/react-kit'
import { isAfter } from 'date-fns'

import {
  formatAmountByCurrency,
  formatOnlyLettersNumbersAndSomeSymbols
} from '../../../../modules/utils/formatters'
import { ATTACHMENT_CATEGORY_IDS, IS_FOREIGN } from '../../Cfmp.constants'

const buildSelectField = ({ input, source, sourceErrorText }) => ({
  errorText: input.error || (source.error ? sourceErrorText : ''),
  input,
  isOtherSelected:
    source.data?.some(option => option.isOther && String(option.value) === String(input.value)) ??
    false,
  source
})

const isFieldValid = field => Boolean(field.value) && !field.error

const useRequiredSelect = () => {
  const select = useSelect({
    changeCallback: updatedValue => {
      if (updatedValue === '') {
        select.setValue(select.value)
        select.onError()
      }
    },
    errorCallbacks: [checkInputErrorRequired()]
  })

  return select
}

/**
 * Encapsulates all field hooks, validation, and exposed form API for CfmpForm.
 */
export const useCfmpForm = ({ attachedFiles, ref, sources }) => {
  const { administrativeActTypes, currencies, procedureTypes, reasonTypes } = sources

  const [attachmentsErrors, setAttachmentsErrors] = useState({})
  const [radiosErrors, setRadiosErrors] = useState({})

  const reasonTypeInput = useSelect({
    changeCallback: updatedValue => {
      if (supplierRut.value) supplierRut.onReset()

      if (updatedValue === '') {
        reasonTypeInput.setValue(reasonTypeInput.value)
        reasonTypeInput.onError()
      }
    },
    errorCallbacks: [checkInputErrorRequired()]
  })

  const administrativeActDate = useDatePicker({
    errorCallbacks: [checkInputErrorRequired()]
  })

  const administrativeActTypeInput = useRequiredSelect()

  const administrativeActTypeDetail = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols]
  })

  const administrativeActNumber = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols]
  })

  const procedureTypeInput = useSelect({
    changeCallback: updatedValue => {
      if (supplierRut.value) supplierRut.onReset()

      if (updatedValue === '') {
        procedureTypeInput.setValue(procedureTypeInput.value)
        procedureTypeInput.onError()
      }
    },
    errorCallbacks: [checkInputErrorRequired()]
  })

  const procedureTypeDetail = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols]
  })

  const administrativeActName = useTextarea({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMaxLength(200)],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols]
  })

  const isForeign =
    reasonTypeInput.value !== IS_FOREIGN.CAUSAL_VALUE &&
    procedureTypeInput.value !== IS_FOREIGN.PROCEDURE_VALUE

  const supplierRut = useInput({
    errorCallbacks: [
      checkInputErrorRequired(),
      isForeign && checkInputErrorDni(),
      checkInputErrorMaxLength(15)
    ].filter(Boolean),
    formatCallbacks: [isForeign && formatDNI].filter(Boolean)
  })

  const supplierName = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols]
  })

  const purchaseReason = useTextarea({
    errorCallbacks: [checkInputErrorRequired(), checkInputErrorMaxLength(500)],
    formatCallbacks: [formatOnlyLettersNumbersAndSomeSymbols]
  })

  const contractStartDate = useDatePicker({
    changeCallback: updatedValue => {
      const isEndDateInvalid =
        contractEndDate.value && updatedValue && !isAfter(contractEndDate.value, updatedValue)

      if (isEndDateInvalid) contractEndDate.onReset()
    },
    errorCallbacks: [checkInputErrorRequired()]
  })

  const contractEndDate = useDatePicker({
    errorCallbacks: [checkInputErrorRequired()]
  })

  const currencyInput = useSelect({
    changeCallback: updatedValue => {
      totalAmount.onReset()

      if (updatedValue === '') {
        currencyInput.setValue(currencyInput.value)
        currencyInput.onError()
      }
    },
    errorCallbacks: [checkInputErrorRequired()]
  })

  const totalAmount = useInput({
    changeCallback: updatedValue => {
      const selectedCurrency = currencies.data?.find(
        option => String(option.value) === String(currencyInput.value)
      )
      const decimals = selectedCurrency?.decimals ?? 0

      totalAmount.setValue(formatAmountByCurrency(updatedValue, decimals))
    },
    errorCallbacks: [checkInputErrorRequired()]
  })

  const environmentalPurchaseInput = useRadio()

  const socialEconomicPurchaseInput = useRadio()

  const administrativeActType = buildSelectField({
    input: administrativeActTypeInput,
    source: administrativeActTypes,
    sourceErrorText: 'Error al obtener el tipo de acto administrativo.'
  })

  const currency = buildSelectField({
    input: currencyInput,
    source: currencies,
    sourceErrorText: 'Error al obtener las monedas.'
  })

  const procedureType = buildSelectField({
    input: procedureTypeInput,
    source: procedureTypes,
    sourceErrorText: 'Error al obtener el tipo de procedimiento.'
  })

  const reasonType = buildSelectField({
    input: reasonTypeInput,
    source: reasonTypes,
    sourceErrorText: 'Error al obtener la causal de la compra.'
  })

  const fields = {
    administrativeActDate,
    administrativeActName,
    administrativeActNumber,
    administrativeActType: administrativeActTypeInput,
    ...(administrativeActType.isOtherSelected && { administrativeActTypeDetail }),
    contractEndDate,
    contractStartDate,
    currency: currencyInput,
    procedureType: procedureTypeInput,
    ...(procedureType.isOtherSelected && { procedureTypeDetail }),
    purchaseReason,
    reasonType: reasonTypeInput,
    supplierName,
    supplierRut,
    totalAmount
  }

  const radioInputs = {
    environmentalPurchase: environmentalPurchaseInput,
    socialEconomicPurchase: socialEconomicPurchaseInput
  }

  const hasCategoryFiles = useCallback(
    categoryId => attachedFiles.some(attached => attached.categoryId === categoryId),
    [attachedFiles]
  )

  const validateAttachments = () => {
    const errors = Object.values(ATTACHMENT_CATEGORY_IDS).reduce(
      (acc, categoryId) => ({
        ...acc,
        [categoryId]: hasCategoryFiles(categoryId)
          ? ''
          : 'Este campo es obligatorio. Debes adjuntar al menos un archivo.'
      }),
      {}
    )

    setAttachmentsErrors(errors)

    return Object.values(errors).every(error => error === '')
  }

  const validateRadios = () => {
    const errors = Object.fromEntries(
      Object.entries(radioInputs).map(([name, input]) => [
        name,
        input.value ? '' : 'Este campo es obligatorio.'
      ])
    )

    setRadiosErrors(errors)

    return Object.values(errors).every(error => error === '')
  }

  useImperativeHandle(ref, () => ({
    getValues: () => ({
      ...Object.fromEntries(Object.entries(fields).map(([name, field]) => [name, field.value])),
      administrativeActTypeDetail: administrativeActType.isOtherSelected
        ? administrativeActTypeDetail.value
        : null,
      procedureTypeDetail: procedureType.isOtherSelected ? procedureTypeDetail.value : null,
      ...Object.fromEntries(Object.entries(radioInputs).map(([name, input]) => [name, input.value]))
    }),

    reset: () => {
      Object.values(fields).forEach(field => field.onReset())
      Object.values(radioInputs).forEach(input => input.onReset())
      setAttachmentsErrors({})
      setRadiosErrors({})
    },

    validate: () => {
      Object.values(fields).forEach(field => field.onError())

      const areFieldsValid = Object.values(fields).every(isFieldValid)
      const areAttachmentsValid = validateAttachments()
      const areRadiosValid = validateRadios()

      return areFieldsValid && areAttachmentsValid && areRadiosValid
    }
  }))

  const displayedAttachmentsErrors = Object.fromEntries(
    Object.entries(attachmentsErrors).map(([categoryId, error]) => [
      categoryId,
      error && !hasCategoryFiles(categoryId) ? error : ''
    ])
  )

  const displayedRadiosErrors = Object.fromEntries(
    Object.entries(radiosErrors).map(([name, error]) => [
      name,
      error && !radioInputs[name].value ? error : ''
    ])
  )

  return {
    administrativeActDate,
    administrativeActName,
    administrativeActNumber,
    administrativeActType,
    administrativeActTypeDetail,
    attachmentsErrors: displayedAttachmentsErrors,
    contractEndDate,
    contractStartDate,
    currency,
    environmentalPurchase: {
      errorText: displayedRadiosErrors.environmentalPurchase || '',
      input: environmentalPurchaseInput
    },
    procedureType,
    procedureTypeDetail,
    purchaseReason,
    reasonType,
    socialEconomicPurchase: {
      errorText: displayedRadiosErrors.socialEconomicPurchase || '',
      input: socialEconomicPurchaseInput
    },
    supplierName,
    supplierRut,
    totalAmount
  }
}
